import DOMPurify from "dompurify"

import {
	filterEmailStyleAttribute,
	filterPresentationalColor,
} from "./style-filter"
import { tidyEmailLayout } from "./tidy"

const ALLOWED_TAGS = [
	"a",
	"abbr",
	"b",
	"big",
	"blockquote",
	"br",
	"caption",
	"center",
	"code",
	"col",
	"colgroup",
	"dd",
	"del",
	"div",
	"dl",
	"dt",
	"em",
	"figcaption",
	"figure",
	"font",
	"h1",
	"h2",
	"h3",
	"h4",
	"h5",
	"h6",
	"hr",
	"i",
	"img",
	"ins",
	"li",
	"mark",
	"ol",
	"p",
	"pre",
	"s",
	"small",
	"span",
	"strike",
	"strong",
	"sub",
	"sup",
	"table",
	"tbody",
	"td",
	"tfoot",
	"th",
	"thead",
	"tr",
	"u",
	"ul",
]

const ALLOWED_ATTR = [
	"align",
	"alt",
	"bgcolor",
	"border",
	"cellpadding",
	"cellspacing",
	"class",
	"color",
	"colspan",
	"dir",
	"height",
	"href",
	"id",
	"rel",
	"rowspan",
	"sizes",
	"src",
	"srcset",
	"style",
	"target",
	"title",
	"valign",
	"width",
]

const FORBID_TAGS = [
	"base",
	"button",
	"embed",
	"form",
	"iframe",
	"input",
	"link",
	"meta",
	"object",
	"script",
	"select",
	"style",
	"svg",
	"textarea",
]

interface Options {
	isDark: boolean
}

/**
 * Renders a message body the way its sender wrote it: tables, spacing and image
 * dimensions survive, only unsafe markup and unreadable colors are rewritten.
 */
export const sanitizeEmailHtml = (
	html: string,
	{ isDark }: Options,
): string => {
	if (!html) return ""

	const filterStyle = (
		_node: Element,
		data: { attrName: string; attrValue: string; keepAttr: boolean },
	) => {
		if (data.attrName !== "style") return
		const filtered = filterEmailStyleAttribute(data.attrValue, isDark)
		if (!filtered) {
			data.keepAttr = false
			return
		}
		data.attrValue = filtered
		data.keepAttr = true
	}

	const normalizeNode = (node: Element) => {
		if (node.tagName === "A") {
			node.setAttribute("target", "_blank")
			node.setAttribute("rel", "noopener noreferrer")
		}

		const appendStyle = (declaration: string) => {
			const existing = node.getAttribute("style")
			node.setAttribute(
				"style",
				existing ? `${existing}; ${declaration}` : declaration,
			)
		}

		const bgcolor = node.getAttribute("bgcolor")
		if (bgcolor !== null) {
			node.removeAttribute("bgcolor")
			const filtered = filterPresentationalColor(bgcolor, "background", isDark)
			if (filtered) appendStyle(`background-color: ${filtered}`)
		}

		const color = node.getAttribute("color")
		if (color !== null) {
			node.removeAttribute("color")
			const filtered = filterPresentationalColor(color, "text", isDark)
			if (filtered) appendStyle(`color: ${filtered}`)
		}
	}

	DOMPurify.addHook("uponSanitizeAttribute", filterStyle)
	DOMPurify.addHook("afterSanitizeAttributes", normalizeNode)
	try {
		const body = DOMPurify.sanitize(html, {
			ALLOWED_TAGS,
			ALLOWED_ATTR,
			FORBID_TAGS,
			ALLOW_DATA_ATTR: false,
			RETURN_DOM: true,
		}) as HTMLElement
		tidyEmailLayout(body)
		return body.innerHTML
	} finally {
		DOMPurify.removeHook("uponSanitizeAttribute")
		DOMPurify.removeHook("afterSanitizeAttributes")
	}
}

// Keeps a pasted signature as the sender's own mail client would send it:
// styles and colors stay, only unsafe markup goes.
export const sanitizeSignatureHtml = (html: string): string =>
	DOMPurify.sanitize(html, {
		ALLOWED_TAGS,
		ALLOWED_ATTR,
		FORBID_TAGS,
		ALLOW_DATA_ATTR: false,
	}).trim()
